import { useDeferredValue, useMemo, useState } from 'react';

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';

import type { Contact } from '@/api/generated';
import { bulkJobsApi, type StartBulkJobInput } from '@/api/queries/bulkJobs';
import { contactsApi, type BulkEditFields } from '@/api/queries/contacts';
import {
  Avatar,
  Button,
  Icon,
  IconButton,
  Pagination,
  SourcePill,
  Table,
  Tag,
  TagMore,
  TagStack,
  colorFromKey,
  initialsOf,
  useToast,
  type TableColumn,
  type TableSort,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useModal } from '@/components/modal/useModal';

import { ContactStatusPills } from '../ContactStatusPills';
import { ratingTone, reviewLabel, reviewTone, tagTone } from '../contactDisplay';
import { getFullName } from '../contactUtils';
import { BulkChangeModal } from './BulkChangeModal';
import { BulkJobProgressDialog } from './BulkJobProgressDialog';
import { ContactQuickPanel } from './ContactQuickPanel';
import { ContactImportFormDialog } from './ContactImportFormDialog';
import { SelectAllBanner } from './SelectAllBanner';
import { ExportHashedDialog } from './ExportHashedDialog';
import { NewContactModal } from '../new/NewContactModal';
import {
  BulkActionBar,
  ContactsToolbar,
  type ColumnVisibilityEntry,
} from './ContactsToolbar';
import { CONTACT_SEARCH_FIELDS } from './ContactSearchFields';
import { conditionsToQuery, parseQuery } from './parseQuery';
import { SavedViewTabs } from './SavedViewTabs';
import type { ContactsSearch } from './searchTypes';
import { useContactDeleteActions } from './useContactDeleteActions';
import { useCanManageContacts } from '../useCanManageContacts';

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  name: true,
  indicators: true,
  email: true,
  phone: true,
  position: true,
  occupation: false,
  interest: true,
  tags: true,
  sources: true,
  attachments: true,
  residence: true,
  rating: true,
  review: true,
  status: true,
  lastUpdated: true,
};

// Maps a sortable table column id to the Mongo field path the backend sorts on
// (POST /contacts/_search `sort`). Array columns (phone/tags/sources) are omitted.
// `attachments` sorts on `attachments.category` — Mongo sorts an array by its min
// element, so this groups records that have attachments vs those that don't
// (a presence/category grouping, not a true count sort).
const CONTACT_SORT_FIELDS: Record<string, string> = {
  name: 'search.name_ascii',
  email: 'contact.primaryEmail',
  position: 'profile.position',
  occupation: 'profile.occupation',
  interest: 'profile.interest',
  attachments: 'attachments.category',
  residence: 'profile.residence',
  rating: 'rating.score',
  review: 'review.status',
  status: 'status.active',
  lastUpdated: 'audit.updatedAt',
};

// Only the Mongo field paths the table renders — slims the list payload by
// dropping the 30+ unused nested fields/arrays (education, employment, …).
/**
 * Grid selection — either explicit row ids (current behavior) or the whole
 * filtered set ("select all in set"). Set mode is all-or-nothing in v1: any
 * checkbox interaction drops back to ids mode.
 */
type GridSelection =
  | { mode: 'ids'; ids: Set<string> }
  | { mode: 'all'; total: number };

const EMPTY_SELECTION: GridSelection = { mode: 'ids', ids: new Set() };

const CONTACT_LIST_FIELDS = [
  'profile.firstName',
  'profile.lastName',
  'profile.titleBefore',
  'profile.titleAfter',
  'profile.interest',
  'profile.position',
  'profile.occupation',
  'profile.residence',
  'profile.city',
  'contact.primaryEmail',
  'contact.phones',
  'tags',
  'sources',
  'rating.score',
  'review.status',
  'status.active',
  'status.doNotContact',
  'audit.updatedAt',
  // Presence indicators + CV pill: subpath projections keep the payload slim
  // (arrays of partial elements, enough for length / category checks).
  'generalNote',
  'comments.kind',
  'attachments.category',
];

export function ContactsGrid() {
  const navigate = useNavigate({ from: '/contacts/' });
  const search = useSearch({ from: '/contacts/' });
  const { openModal, closeModal } = useModal();
  const { handleDeleteSingle, handleBulkDelete } = useContactDeleteActions();
  const canManage = useCanManageContacts();
  const bulkSetReview = contactsApi.useBulkSetReview();
  const bulkSetActive = contactsApi.useBulkSetActive();
  const bulkEditFields = contactsApi.useBulkEditFields();
  const startBulkJob = bulkJobsApi.useStartBulkJob();
  const { show: showToast } = useToast();

  // The whole filter lives in the URL as `q` (a compact query string) and is the
  // single source of truth — parsed into the condition tree the search engine runs.
  const conditions = useMemo(
    () => parseQuery(search.q ?? '', CONTACT_SEARCH_FIELDS),
    [search.q],
  );

  const deferredSearch = useDeferredValue(search);
  const deferredConditions = useMemo(
    () => parseQuery(deferredSearch.q ?? '', CONTACT_SEARCH_FIELDS),
    [deferredSearch.q],
  );
  const isPending = deferredSearch !== search;

  // Sort lives in the URL (single source of truth) so it survives pagination and
  // is sent to the backend. `sortBy` is a column id; map it to a Mongo field path.
  const sort: TableSort | null = search.sortBy
    ? { id: search.sortBy, direction: search.sortDir ?? 'asc' }
    : null;
  const listOptions = useMemo(() => {
    const sortField = deferredSearch.sortBy ? CONTACT_SORT_FIELDS[deferredSearch.sortBy] : undefined;
    return {
      sort: sortField
        ? { [sortField]: (deferredSearch.sortDir === 'desc' ? -1 : 1) as 1 | -1 }
        : undefined,
      fields: CONTACT_LIST_FIELDS,
    };
  }, [deferredSearch.sortBy, deferredSearch.sortDir]);

  const { data: contacts } = contactsApi.useList(
    deferredSearch,
    deferredConditions,
    deferredSearch.query,
    listOptions,
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(DEFAULT_COLUMN_VISIBILITY);
  const [selection, setSelection] = useState<GridSelection>(EMPTY_SELECTION);

  // A set selection is defined by the filter — when the filter or quick search
  // changes, the selection no longer describes what the user confirmed. Reset
  // during render (https://react.dev/learn/you-might-not-need-an-effect).
  const filterKey = `${search.q ?? ''}\0${search.query ?? ''}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setSelection(EMPTY_SELECTION);
  }

  const navigateSearch = (next: ContactsSearch) => {
    navigate({ search: () => next });
  };

  const handleSortChange = (next: TableSort | null) => {
    navigateSearch({
      ...search,
      sortBy: next?.id,
      sortDir: next?.direction,
      page: 0,
    });
  };

  const openExportDialog = () => {
    openModal(
      <ExportHashedDialog
        conditions={conditions}
        query={search.query}
        // In set mode the export falls back to the filter scope (query +
        // conditions), which is exactly what "all matching" means.
        selectedIds={selection.mode === 'ids' ? Array.from(selection.ids) : []}
      />,
      { size: 'lg' },
    );
  };

  const total = contacts.pagination?.total ?? 0;

  // ---- "Select all in set" bulk operations (run as background jobs) ----
  const hasConditions = (conditions.conditions?.length ?? 0) > 0;
  const trimmedQuery = search.query?.trim() ? search.query.trim() : undefined;

  /**
   * Enqueues a filter-based bulk job and opens the blocking progress dialog
   * (driven by the SignalR jobs hub). `deferDialog` escapes ConfirmDialog's
   * own closeModal(), which runs right after onConfirm and would otherwise
   * close the freshly opened progress dialog (single-modal provider).
   */
  const startSetOperation = async (
    input: Pick<StartBulkJobInput, 'operation' | 'status' | 'mode' | 'active'>,
    opts?: { deferDialog?: boolean },
  ) => {
    try {
      const job = await startBulkJob.mutateAsync({
        ...input,
        query: trimmedQuery,
        conditions: hasConditions ? conditions : undefined,
      });
      setSelection(EMPTY_SELECTION);
      const openProgress = () =>
        openModal(<BulkJobProgressDialog jobId={job.id} initialTotal={job.total} />, {
          closeOnScrim: false,
        });
      if (opts?.deferDialog) setTimeout(openProgress, 0);
      else openProgress();
    } catch (e) {
      const serverError = isAxiosError(e)
        ? (e.response?.data as { error?: string } | undefined)?.error
        : undefined;
      showToast({
        severity: 'error',
        title: 'Could not start bulk operation',
        description: serverError ?? 'Please try again.',
      });
    }
  };

  const handleDeleteAll = () => {
    openModal(
      <ConfirmDialog
        title={`Delete ${total.toLocaleString()} Contacts`}
        message={`Are you sure you want to delete all ${total.toLocaleString()} contacts matching the current filter? Contacts added while the operation runs may be included. This action cannot be undone.`}
        confirmLabel="Delete all"
        onConfirm={() => startSetOperation({ operation: 'delete' }, { deferDialog: true })}
      />,
    );
  };

  const columns: TableColumn<Contact>[] = useMemo(() => {
    return [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => {
          const fullName = getFullName(row.profile);
          return (
            <div className="cell-name">
              <Avatar
                initials={initialsOf(row.profile?.firstName, row.profile?.lastName)}
                color={colorFromKey(row.id ?? fullName)}
                size={26}
              />
              <Link
                to="/contacts/$id"
                params={{ id: row.id ?? '' }}
                className="name-link"
                // Don't let the row's quick-view drawer swallow the click — the
                // name link always navigates to the contact detail.
                onClick={(e) => e.stopPropagation()}
              >
                {fullName}
              </Link>
            </div>
          );
        },
        hidden: !columnVisibility.name,
      },
      {
        id: 'indicators',
        header: '',
        width: 64,
        cell: (row) => {
          const hasNote = !!row.generalNote?.trim();
          const hasComments = (row.comments?.length ?? 0) > 0;
          if (!hasNote && !hasComments) return null;
          return (
            <span style={{ display: 'inline-flex', gap: 6, color: 'var(--text-3)' }}>
              {hasNote && (
                <span title="Has general note" style={{ display: 'inline-flex' }}>
                  <Icon name="note" size={14} />
                </span>
              )}
              {hasComments && (
                <span
                  title={`${row.comments?.length} comment${(row.comments?.length ?? 0) > 1 ? 's' : ''}`}
                  style={{ display: 'inline-flex' }}
                >
                  <Icon name="message" size={14} />
                </span>
              )}
            </span>
          );
        },
        hidden: !columnVisibility.indicators,
      },
      {
        id: 'email',
        header: 'Email',
        sortable: true,
        cell: (row) => row.contact?.primaryEmail ?? <Muted />,
        hidden: !columnVisibility.email,
      },
      {
        id: 'phone',
        header: 'Phone',
        cell: (row) => row.contact?.phones?.[0]?.number ?? <Muted />,
        hidden: !columnVisibility.phone,
      },
      {
        id: 'position',
        header: 'Position',
        sortable: true,
        cell: (row) => row.profile?.position ?? <Muted />,
        hidden: !columnVisibility.position,
      },
      {
        id: 'occupation',
        header: 'Occupation',
        sortable: true,
        cell: (row) => row.profile?.occupation ?? <Muted />,
        hidden: !columnVisibility.occupation,
      },
      {
        id: 'interest',
        header: 'Interest',
        sortable: true,
        cell: (row) => row.profile?.interest ?? <Muted />,
        hidden: !columnVisibility.interest,
      },
      {
        id: 'tags',
        header: 'Tags',
        cell: (row) => {
          if (!row.tags || row.tags.length === 0) return <Muted />;
          const visible = row.tags.slice(0, 2);
          const overflow = row.tags.length - visible.length;
          return (
            <TagStack>
              {visible.map((t) => (
                <Tag key={t} tone={tagTone(t)}>
                  {t}
                </Tag>
              ))}
              {overflow > 0 && <TagMore count={overflow} />}
            </TagStack>
          );
        },
        hidden: !columnVisibility.tags,
      },
      {
        id: 'sources',
        header: 'Sources',
        cell: (row) => {
          if (!row.sources || row.sources.length === 0) return <Muted />;
          const visible = row.sources.slice(0, 2);
          const overflow = row.sources.length - visible.length;
          return (
            <TagStack>
              {visible.map((s) => (
                <SourcePill key={s} label={s} color={colorFromKey(s)} />
              ))}
              {overflow > 0 && <TagMore count={overflow} />}
            </TagStack>
          );
        },
        hidden: !columnVisibility.sources,
      },
      {
        id: 'attachments',
        header: 'Attachments',
        sortable: true,
        width: 110,
        cell: (row) => {
          const hasCv = row.attachments?.some((a) => a.category === 'cv');
          if (!hasCv) return <Muted />;
          return <Tag tone="brand">CV</Tag>;
        },
        hidden: !columnVisibility.attachments,
      },
      {
        id: 'residence',
        header: 'Residence',
        sortable: true,
        cell: (row) => row.profile?.residence ?? <Muted />,
        hidden: !columnVisibility.residence,
      },
      {
        id: 'rating',
        header: 'Rating',
        sortable: true,
        width: 90,
        cell: (row) => {
          const score = row.rating?.score;
          if (score === undefined || score === null) return <Muted />;
          return <Tag tone={ratingTone(score)}>{Math.round(score)}</Tag>;
        },
        hidden: !columnVisibility.rating,
      },
      {
        id: 'review',
        header: 'Review',
        sortable: true,
        width: 130,
        cell: (row) => {
          const label = reviewLabel(row.review?.status);
          if (!label) return <Muted />;
          return <Tag tone={reviewTone(row.review?.status)}>{label}</Tag>;
        },
        hidden: !columnVisibility.review,
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        width: 200,
        cell: (row) => <ContactStatusPills status={row.status} />,
        hidden: !columnVisibility.status,
      },
      {
        id: 'lastUpdated',
        header: 'Last updated',
        sortable: true,
        width: 160,
        cell: (row) => {
          const ts = row.audit?.updatedAt;
          if (!ts) return <Muted />;
          const d = new Date(ts);
          if (Number.isNaN(d.getTime())) return <Muted />;
          return (
            <span title={d.toISOString()} style={{ color: 'var(--text-2)' }}>
              {format(d, 'd MMM yyyy, HH:mm')}
            </span>
          );
        },
        hidden: !columnVisibility.lastUpdated,
      },
    ];
  }, [columnVisibility]);

  const columnEntries: ColumnVisibilityEntry[] = columns.map((col) => ({
    id: col.id,
    label: typeof col.header === 'string' ? col.header : col.id,
    visible: !col.hidden,
    toggleable: col.id !== 'name',
  }));

  const sortableFields = useMemo(
    () =>
      columns
        .filter((c) => c.sortable && !c.hidden)
        .map((c) => ({ id: c.id, label: typeof c.header === 'string' ? c.header : c.id })),
    [columns],
  );

  // Rows arrive already sorted by the backend (POST /contacts/_search `sort`).
  const rows = contacts.data ?? [];

  const pageIds = rows.map((row) => row.id ?? '');
  const selectedCount = selection.mode === 'all' ? total : selection.ids.size;
  // In set mode every visible row renders checked; any checkbox interaction
  // routes through onChange below, which drops back to ids mode.
  const selectedIdsForTable = selection.mode === 'all' ? new Set(pageIds) : selection.ids;
  const pageFullySelected =
    selection.mode === 'ids' && rows.length > 0 && pageIds.every((id) => selection.ids.has(id));
  const showSelectAllBanner =
    selection.mode === 'all' || (pageFullySelected && total > rows.length);

  // Bulk field change — id selections run synchronously (POST /contacts/_bulk/edit);
  // a whole-set selection runs as a background job with the same live progress
  // dialog as the other set operations. The modal calls this and rejects surface
  // inside it.
  const openBulkChange = () => {
    const applyBulkChange = async (fields: BulkEditFields) => {
      try {
        if (selection.mode === 'all') {
          const job = await startBulkJob.mutateAsync({
            operation: 'edit',
            fields,
            query: trimmedQuery,
            conditions: hasConditions ? conditions : undefined,
          });
          setSelection(EMPTY_SELECTION);
          openModal(<BulkJobProgressDialog jobId={job.id} initialTotal={job.total} />, {
            closeOnScrim: false,
          });
        } else {
          const ids = Array.from(selection.ids);
          await bulkEditFields.mutateAsync({ ids, fields });
          setSelection(EMPTY_SELECTION);
          showToast({
            severity: 'success',
            title: 'Bulk change applied',
            description: `${ids.length} contact${ids.length === 1 ? '' : 's'} updated.`,
          });
          closeModal();
        }
      } catch (e) {
        // Surface the server's message (e.g. "a bulk operation is already running")
        // inside the modal by rethrowing a translated Error.
        const serverError = isAxiosError(e)
          ? (e.response?.data as { error?: string } | undefined)?.error
          : undefined;
        throw new Error(serverError ?? (e instanceof Error ? e.message : 'Bulk change failed'));
      }
    };
    openModal(<BulkChangeModal count={selectedCount} onApply={applyBulkChange} />);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">Contacts</h1>
          <div className="page-subtitle">
            {total === 0
              ? 'No records yet'
              : `${contacts.data?.length ?? 0} of ${total.toLocaleString()} records`}
          </div>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            leftIcon={<Icon name="upload" size={13} />}
            onClick={() => openModal(<ContactImportFormDialog />)}
          >
            Import
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Icon name="download" size={13} />}
            onClick={openExportDialog}
          >
            Export
          </Button>
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={13} />}
            onClick={() => navigateSearch({ ...search, new: 1 })}
          >
            New contact
          </Button>
        </div>
      </header>

      <SavedViewTabs
        search={search}
        total={total}
        onSelect={(next) => {
          setSelection(EMPTY_SELECTION);
          navigateSearch(next);
        }}
      />

      {selectedCount > 0 ? (
        <BulkActionBar
          count={selectedCount}
          scopeLabel={
            selection.mode === 'all'
              ? `All ${total.toLocaleString()} matching selected`
              : undefined
          }
          onClear={() => setSelection(EMPTY_SELECTION)}
          onBulkChange={openBulkChange}
          onDelete={
            canManage
              ? () => {
                  if (selection.mode === 'all') {
                    handleDeleteAll();
                  } else {
                    handleBulkDelete(Array.from(selection.ids));
                    setSelection(EMPTY_SELECTION);
                  }
                }
              : undefined
          }
          onFlagReview={() => {
            if (selection.mode === 'all') {
              void startSetOperation({ operation: 'review', status: 'requiresReview', mode: 'manual' });
              return;
            }
            bulkSetReview.mutate({
              ids: Array.from(selection.ids),
              status: 'requiresReview',
              mode: 'manual',
            });
            setSelection(EMPTY_SELECTION);
          }}
          onFlagAutoReview={
            canManage
              ? () => {
                  if (selection.mode === 'all') {
                    void startSetOperation({ operation: 'review', status: 'requiresReview', mode: 'automatic' });
                    return;
                  }
                  bulkSetReview.mutate({
                    ids: Array.from(selection.ids),
                    status: 'requiresReview',
                    mode: 'automatic',
                  });
                  setSelection(EMPTY_SELECTION);
                }
              : undefined
          }
          onStartAutoInvestigation={
            canManage
              ? () => {
                  if (selection.mode === 'all') {
                    void startSetOperation({ operation: 'review', status: 'autoInvestigation', mode: 'automatic' });
                    return;
                  }
                  bulkSetReview.mutate({
                    ids: Array.from(selection.ids),
                    status: 'autoInvestigation',
                    mode: 'automatic',
                  });
                  setSelection(EMPTY_SELECTION);
                }
              : undefined
          }
          onActivate={
            canManage
              ? () => {
                  if (selection.mode === 'all') {
                    void startSetOperation({ operation: 'status', active: true });
                    return;
                  }
                  bulkSetActive.mutate({ ids: Array.from(selection.ids), active: true });
                  setSelection(EMPTY_SELECTION);
                }
              : undefined
          }
          onDeactivate={
            canManage
              ? () => {
                  if (selection.mode === 'all') {
                    void startSetOperation({ operation: 'status', active: false });
                    return;
                  }
                  bulkSetActive.mutate({ ids: Array.from(selection.ids), active: false });
                  setSelection(EMPTY_SELECTION);
                }
              : undefined
          }
          onExport={openExportDialog}
        />
      ) : (
        <ContactsToolbar
          columns={columnEntries}
          onColumnToggle={(id) =>
            setColumnVisibility((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }))
          }
          query={search.query ?? ''}
          onQueryChange={(v) => navigateSearch({ ...search, query: v || undefined, page: 0 })}
          conditions={conditions}
          conditionFields={CONTACT_SEARCH_FIELDS}
          onConditionsChange={(next) =>
            navigateSearch({
              ...search,
              q: conditionsToQuery(next, CONTACT_SEARCH_FIELDS) || undefined,
              page: 0,
            })
          }
          sortableFields={sortableFields}
          sort={sort}
          onSortChange={handleSortChange}
        />
      )}

      {showSelectAllBanner && (
        <SelectAllBanner
          mode={selection.mode === 'all' ? 'all' : 'offer'}
          pageCount={rows.length}
          total={total}
          onSelectAll={() => setSelection({ mode: 'all', total })}
          onClear={() => setSelection(EMPTY_SELECTION)}
        />
      )}

      {/* Row click opens the quick-view drawer (general note + comments) over the
          content — selected id lives in the URL like the audit-logs panel. The
          name link / checkboxes / row actions stop propagation and keep working. */}
      <Table<Contact>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id ?? ''}
        loading={isPending}
        emptyMessage="No contacts match your filters"
        sort={sort}
        onSortChange={handleSortChange}
        onRowClick={(row) =>
          navigateSearch({
            ...search,
            selected: row.id && row.id !== search.selected ? row.id : undefined,
          })
        }
        selection={{
          selectedIds: selectedIdsForTable,
          // Any checkbox interaction collapses a set selection back to ids.
          onChange: (ids) => setSelection({ mode: 'ids', ids }),
        }}
        rowActions={(row) => (
          <>
            <IconButton
              icon="eye"
              iconSize={13}
              size="sm"
              label="View contact"
              className="row-action"
              onClick={() => navigate({ to: '/contacts/$id', params: { id: row.id ?? '' } })}
            />
            <IconButton
              icon="edit"
              iconSize={13}
              size="sm"
              label="Edit contact"
              className="row-action"
              onClick={() => navigate({ to: '/contacts/$id', params: { id: row.id ?? '' } })}
            />
            {canManage && (
              <IconButton
                icon="trash"
                iconSize={13}
                size="sm"
                label="Delete contact"
                className="row-action danger"
                onClick={() => handleDeleteSingle(row)}
              />
            )}
          </>
        )}
      />

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius-md) var(--radius-md)' }}>
        <Pagination
          page={search.page}
          pageSize={search.pageSize}
          total={total}
          onPageChange={(page) => navigateSearch({ ...search, page })}
          onPageSizeChange={(pageSize) => navigateSearch({ ...search, pageSize, page: 0 })}
        />
      </div>

      {search.selected && (
        <ContactQuickPanel
          contactId={search.selected}
          onClose={() => navigateSearch({ ...search, selected: undefined })}
        />
      )}

      <NewContactModal
        open={search.new === 1}
        onClose={() => navigateSearch({ ...search, new: undefined })}
      />
    </div>
  );
}

function Muted() {
  return <span style={{ color: 'var(--text-4)' }}>—</span>;
}

